"use client";

import React, { useState, useRef, useCallback, useEffect } from "react";
import { useFreemium } from "@/lib/freemium-context";
import { FIXED_COSTS, PRO_OPTIMAL } from "@/lib/credit-costs";
import {
  Upload,
  FileText,
  Loader2,
  Download,
  RotateCcw,
  LayoutGrid,
  BookOpen,
  Brain,
  FileBarChart,
  X,
  CheckCircle2,
  Paperclip,
  PenLine,
  Sparkles,
} from "lucide-react";
import { Textarea } from "@/components/ui/textarea";
import {
  AlertDialog, AlertDialogContent, AlertDialogHeader, AlertDialogTitle,
  AlertDialogDescription, AlertDialogFooter, AlertDialogCancel, AlertDialogAction,
} from "@/components/ui/alert-dialog";
import { toast } from "sonner";
import { exportToWord } from "@/lib/export-word";
import { friendlyError } from "@/lib/error-messages";
import { AUTH_RESUME_SETTLE_MS } from "@/lib/auth-resume";
import { useModuleDocuments } from "@/hooks/use-module-documents";
import ModuleHistoryPanel from "@/components/transqual/ModuleHistoryPanel";
import { loadPersistedResult, savePersistedResult, clearPersistedResult } from "@/lib/result-persistence";
import { CreditCostBanner } from "@/components/CreditCostBanner";
import { REFERENCE_PRICE_LABEL } from "@/lib/pricing-display";

// ─── Types ───────────────────────────────────────────────────────────────────

type AnalysisMethod = "thematic-content" | "narrative" | "ipa" | "grounded-theory";
type StructureMode = "ai" | "manual" | "guide";

interface AnalysisMethodOption {
  id: AnalysisMethod;
  title: string;
  framework: string;
  description: string;
  icon: React.ElementType;
}

interface UploadedFile {
  name: string;
  content: string;
  size: number;
}

interface AnalysisResponse {
  success: boolean;
  text: string;
}

// ─── Constants ───────────────────────────────────────────────────────────────

const ANALYSIS_METHODS: AnalysisMethodOption[] = [
  {
    id: "thematic-content",
    title: "Analyse thématique de contenu",
    framework: "Braun & Clarke, 2006 ; Bardin, 1977",
    description: "Méthode qualitative reposant sur l'identification, le codage et le regroupement d'unités de sens afin de dégager et d'interpréter des thèmes récurrents au sein d'un corpus.",
    icon: LayoutGrid,
  },
  {
    id: "narrative",
    title: "Analyse narrative",
    framework: "Riessman, 2008",
    description: "Analyse de la manière dont les répondants construisent le sens de leurs expériences à travers le récit. Idéale pour les récits de vie, histoires de réussite ou d'échec.",
    icon: BookOpen,
  },
  {
    id: "ipa",
    title: "Analyse phénoménologique (IPA)",
    framework: "Smith, 1995",
    description: "Analyse approfondie de la manière dont les individus perçoivent et donnent sens à une expérience particulière. Du particulier au général.",
    icon: Brain,
  },
  {
    id: "grounded-theory",
    title: "Théorisation ancrée",
    framework: "Glaser & Strauss, 1967",
    description: "Construction d'une théorie émergente directement ancrée dans les données. Identifie le phénomène central et ses relations causales.",
    icon: Sparkles,
  },
];

// ─── Component ───────────────────────────────────────────────────────────────

// eslint-disable-next-line @typescript-eslint/no-unused-vars
export default function Module03({ compact: _compact, onHasResult }: { compact?: boolean; onHasResult?: (has: boolean) => void } = {}) {
  // ---- State ----
  const [activeInput, setActiveInput] = useState<"upload" | "paste">("upload");
  const [verbatimText, setVerbatimText] = useState("");
  const [uploadedFiles, setUploadedFiles] = useState<UploadedFile[]>([]);
  const [structureMode, setStructureMode] = useState<StructureMode>("ai");
  const [manualThemes, setManualThemes] = useState("");
  const [guideFile, setGuideFile] = useState<UploadedFile | null>(null);
  const [selectedMethod, setSelectedMethod] = useState<AnalysisMethod>("thematic-content");
  const [isLoading, setIsLoading] = useState(false);
  const [result, setResult] = useState<string | null>(null);
  const [isApercu, setIsApercu] = useState(false);
  const [isExporting, setIsExporting] = useState(false);
  const [step, setStep] = useState(1);
  const [uploadProgress, setUploadProgress] = useState<{ done: number; total: number } | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const guideInputRef = useRef<HTMLInputElement>(null);

  const { limits, incrementLimit, openUpgrade, openAuth, openAuthGate, isAuthenticated } = useFreemium();
  // Sauvegarde auto + historique 48 h (anti-perte).
  const { history, saveDocument } = useModuleDocuments('rapport', isAuthenticated);
  // Permet d'annuler l'analyse en cours via le bouton « Annuler ».
  const analyzeAbortRef = useRef<AbortController | null>(null);
  // RAPPORT RÉSERVÉ AU PRO depuis le 2026-10-12 (`PRO_ONLY_MODULES`). La
  // condition se réduit donc au plan : ni le verrou `free_module_used` ni le
  // solde n'entrent plus en jeu, puisqu'aucun plan gratuit ne peut produire de
  // rapport, quel que soit son solde.
  // IMPORTANT, inchangé : ne s'applique qu'aux utilisateurs CONNECTÉS. Un
  // visiteur anonyme doit pouvoir lancer pour déclencher la pop-up
  // d'inscription — sinon il est bloqué à tort, sans comprendre pourquoi.
  const rapportBlocked = isAuthenticated && !limits.is_pro;
  // Conseil d'optimisation : suggérer plus d'entretiens pour rentabiliser le
  // forfait fixe. Le module étant réservé au Pro, le seul optimum qui ait un
  // sens est celui du Pro — les bornes du plan gratuit ont été retirées, elles
  // auraient conseillé un plafond que personne ne peut plus atteindre.
  const verbatimCount = (verbatimText.trim() ? 1 : 0) + uploadedFiles.length;
  const tipMaxEntretiens = PRO_OPTIMAL.analysisMaxEntretiens;
  const tipFlatCost = FIXED_COSTS.analysis; // tarif Pro, seul applicable
  const showOptimizationTip =
    verbatimCount >= 1 &&
    !rapportBlocked &&
    verbatimCount < PRO_OPTIMAL.suggestThreshold; // 1-9 entretiens

  // Lancement en attente : un visiteur non connecté a cliqué sur « Générer mon
  // rapport », la popup d'inscription s'est affichée. Une fois connecté, on
  // relance — les verbatims/fichiers déjà chargés restent en mémoire.
  const [pendingAuthLaunch, setPendingAuthLaunch] = useState(false);
  const isUploading = uploadProgress !== null && uploadProgress.done < uploadProgress.total;
  const uploadAllDone = uploadProgress !== null && uploadProgress.done === uploadProgress.total;

  // ---- Computed ----
  const hasVerbatims = verbatimText.trim().length > 0 || uploadedFiles.length > 0;

  useEffect(() => { onHasResult?.(result !== null) }, [result, onHasResult])

  // Persistance localStorage — Module 04 (Rapport d'analyse).
  // Sauvegarde le rapport complet + la méthode choisie. Pas de fichiers source.
  // Les aperçus freemium ne sont pas persistés (teaser, pas un livrable).
  const REPORT_LS_KEY = "qualyse_module04_result";
  const [restoredFromCache, setRestoredFromCache] = useState(false);
  const [reportSavedAt, setReportSavedAt] = useState<number | null>(null);
  // Au chargement de la page : si un rapport existe en cache, on le restaure
  // ET on saute directement à l'étape 4 pour l'afficher (téléchargement +
  // sommaire) sans forcer l'utilisateur à repasser toutes les étapes.
  useEffect(() => {
    const saved = loadPersistedResult<{ result: string; method?: AnalysisMethod }>(REPORT_LS_KEY);
    if (saved?.data?.result) {
      setResult(saved.data.result);
      if (saved.data.method) setSelectedMethod(saved.data.method);
      setReportSavedAt(saved.createdAt);
      setRestoredFromCache(true);
      setStep(4);
    }
  }, []);
  useEffect(() => {
    if (!result || isApercu) return;
    savePersistedResult(REPORT_LS_KEY, { result, method: selectedMethod });
  }, [result, selectedMethod, isApercu]);

  const dismissRestoredBanner = useCallback(() => {
    setRestoredFromCache(false);
    setReportSavedAt(null);
    setResult(null);
    clearPersistedResult(REPORT_LS_KEY);
    toast.info("Résultat précédent effacé.");
  }, []);

  const collectedVerbatims: string[] = [];
  if (verbatimText.trim()) collectedVerbatims.push(verbatimText.trim());
  uploadedFiles.forEach((f) => { if (f.content.trim()) collectedVerbatims.push(f.content.trim()); });

  // ---- File reading helper ----
  const readFileContent = useCallback(async (file: File): Promise<string> => {
    const ext = file.name.split(".").pop()?.toLowerCase() ?? "";
    if (ext === "pdf") {
      const pdfjsLib = await import("pdfjs-dist");
      pdfjsLib.GlobalWorkerOptions.workerSrc = new URL(
        "pdfjs-dist/build/pdf.worker.min.mjs",
        import.meta.url
      ).toString();
      const buffer = await file.arrayBuffer();
      const pdf = await pdfjsLib.getDocument({ data: buffer }).promise;
      const pages: string[] = [];
      for (let p = 1; p <= pdf.numPages; p++) {
        const page = await pdf.getPage(p);
        const tc = await page.getTextContent();
        pages.push(tc.items.map((it) => ("str" in it ? it.str : "")).join(" "));
      }
      return pages.join("\n");
    }
    if (ext === "docx" || ext === "doc") {
      const mammoth = await import("mammoth");
      const buffer = await file.arrayBuffer();
      const res = await mammoth.default.extractRawText({ arrayBuffer: buffer });
      return res.value;
    }
    return await file.text();
  }, []);

  // ---- Verbatim file upload (.docx, .xlsx only) ----
  const handleFileUpload = useCallback(
    async (e: React.ChangeEvent<HTMLInputElement>) => {
      const files = e.target.files;
      if (!files || files.length === 0) return;

      // RAPPORT RÉSERVÉ AU PRO. L'ancien plafond « 5 entretiens pour le plan
      // gratuit » est retiré : ce n'était plus le NOMBRE qui bloquait, mais le
      // plan — annoncer un plafond aurait laissé croire qu'un rapport gratuit
      // reste possible en déposant moins de fichiers. On prévient donc tôt,
      // avec la vraie raison, plutôt que d'accepter des dépôts qui ne seront
      // jamais traités.
      // `rapportBlocked` est faux pour un visiteur NON connecté : il peut
      // déposer, et la popup d'inscription se déclenchera au lancement.
      if (rapportBlocked) {
        toast.error("Le rapport d'analyse est réservé au plan Pro.");
        openUpgrade();
        if (fileInputRef.current) fileInputRef.current.value = "";
        return;
      }

      // Pre-filter valid files to know the total upfront
      const validFiles: { file: File; ext: string }[] = [];
      for (let i = 0; i < files.length; i++) {
        const file = files[i];
        const ext = file.name.split(".").pop()?.toLowerCase() ?? "";
        if (!["docx", "xlsx"].includes(ext)) {
          toast.error(`"${file.name}" — seuls les formats .docx et .xlsx sont acceptés.`);
          continue;
        }
        if (file.size > 30 * 1024 * 1024) {
          toast.error(`"${file.name}" dépasse la limite de 30 Mo.`);
          continue;
        }
        validFiles.push({ file, ext });
      }
      if (validFiles.length === 0) { if (fileInputRef.current) fileInputRef.current.value = ""; return; }

      setUploadProgress({ done: 0, total: validFiles.length });
      const newFiles: UploadedFile[] = [];
      for (let i = 0; i < validFiles.length; i++) {
        const { file, ext } = validFiles[i];
        try {
          let content = "";
          if (ext === "xlsx") {
            const buffer = await file.arrayBuffer();
            const bytes = new Uint8Array(buffer);
            const decoder = new TextDecoder("utf-8", { fatal: false });
            const raw = decoder.decode(bytes);
            const textMatches = raw.match(/<t[^>]*>([^<]+)<\/t>/g);
            if (textMatches && textMatches.length > 0) {
              content = textMatches.map(m => m.replace(/<[^>]+>/g, "")).filter(s => s.trim()).join(" | ");
            } else {
              content = raw.replace(/<[^>]+>/g, " ").replace(/\s+/g, " ").trim().slice(0, 50000);
            }
          } else {
            content = await readFileContent(file);
          }
          if (!content.trim()) { toast.error(`"${file.name}" semble vide ou non lisible.`); }
          else newFiles.push({ name: file.name, content, size: file.size });
        } catch {
          toast.error(`Impossible de lire "${file.name}".`);
        }
        setUploadProgress({ done: i + 1, total: validFiles.length });
      }
      if (newFiles.length > 0) setUploadedFiles((prev) => [...prev, ...newFiles]);
      if (fileInputRef.current) fileInputRef.current.value = "";
    },
    [readFileContent, limits.is_pro, uploadedFiles.length, openUpgrade]
  );

  const removeFile = useCallback((index: number) => {
    setUploadedFiles((prev) => prev.filter((_, i) => i !== index));
  }, []);

  // ---- Guide file upload ----
  const handleGuideUpload = useCallback(
    async (e: React.ChangeEvent<HTMLInputElement>) => {
      const file = e.target.files?.[0];
      if (!file) return;
      const ext = file.name.split(".").pop()?.toLowerCase() ?? "";
      if (!["txt", "docx", "doc", "pdf"].includes(ext)) {
        toast.error("Format non supporté. Utilisez .docx, .pdf ou .txt.");
        return;
      }
      try {
        const content = await readFileContent(file);
        if (!content.trim()) { toast.error("Le guide semble vide."); return; }
        setGuideFile({ name: file.name, content, size: file.size });
        toast.success("Guide d'entretien chargé.");
      } catch {
        toast.error("Impossible de lire le guide.");
      }
      if (guideInputRef.current) guideInputRef.current.value = "";
    },
    [readFileContent]
  );

  // ---- Analysis ----
  const handleLaunchAnalysis = useCallback(async () => {
    if (!hasVerbatims) return;
    // Inscription obligatoire avant tout traitement : on affiche la popup et on
    // garde le lancement en attente — les verbatims/fichiers déjà chargés restent intacts.
    if (!isAuthenticated) {
      setPendingAuthLaunch(true);
      openAuthGate();
      return;
    }
    setIsLoading(true);
    setResult(null);
    setIsApercu(false);
    setRestoredFromCache(false);
    // Permet d'interrompre la requête en cours (bouton « Annuler »).
    const controller = new AbortController();
    analyzeAbortRef.current = controller;

    if (rapportBlocked) {
      try {
        const response = await fetch("/api/analyze", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            type: "analysis",
            mode: "aperçu",
            analysisMethod: selectedMethod,
            verbatims: collectedVerbatims,
            researchQuestion: "Identifie les thèmes principaux.",
            reportFormat: "academic",
          }),
          signal: controller.signal,
        });
        if (!response.ok) throw new Error(`Erreur serveur (${response.status})`);
        const data: AnalysisResponse = await response.json();
        if (data.success && data.text) {
          setResult(data.text);
          setIsApercu(true);
        } else {
          throw new Error("Réponse inattendue du serveur.");
        }
      } catch (err: unknown) {
        if (controller.signal.aborted || (err instanceof Error && err.name === 'AbortError')) {
          toast.info('Analyse annulée.');
        } else {
          toast.error(friendlyError(err, 'processing'));
        }
      } finally {
        setIsLoading(false);
        analyzeAbortRef.current = null;
      }
      return;
    }

    let researchQuestion = "";
    let cadragePayload: Record<string, string> = {};
    if (structureMode === "guide" && guideFile) {
      researchQuestion = "Structurer l'analyse selon le document de cadrage fourni par l'utilisateur.";
      cadragePayload = {
        structureMode: "guide",
        cadrageDocument: guideFile.content,
        cadrageFilename: guideFile.name,
      };
    } else if (structureMode === "manual" && manualThemes.trim()) {
      researchQuestion = "Structurer l'analyse selon les thèmes définis manuellement par l'utilisateur.";
      cadragePayload = {
        structureMode: "manual",
        userThemes: manualThemes.trim(),
      };
    } else {
      researchQuestion = "Identifie toi-même les thèmes depuis les données et structure l'analyse en conséquence.";
      cadragePayload = { structureMode: "ai" };
    }

    try {
      const response = await fetch("/api/analyze", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          type: "analysis",
          analysisMethod: selectedMethod,
          verbatims: collectedVerbatims,
          researchQuestion,
          reportFormat: "academic",
          ...cadragePayload,
        }),
        signal: controller.signal,
      });
      if (!response.ok) {
        const errorData = await response.json().catch(() => null) as { error?: string } | null;
        throw new Error(errorData?.error || `Erreur serveur (${response.status})`);
      }
      const reader = response.body!.getReader();
      const decoder = new TextDecoder();
      let fullText = '';
      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        fullText += decoder.decode(value, { stream: true });
      }
      fullText += decoder.decode();
      if (fullText.trim()) {
        setResult(fullText);
        await incrementLimit('rapport_count');
        // Sauvegarde automatique (anti-perte + historique 48 h). Non bloquant.
        const docTitle = `${ANALYSIS_METHODS.find(mt => mt.id === selectedMethod)?.title ?? 'Rapport'}${verbatimCount > 0 ? ` — ${verbatimCount} source${verbatimCount > 1 ? 's' : ''}` : ''}`;
        void saveDocument(docTitle, fullText);
        toast.success("Rapport généré avec succès.");
      } else {
        throw new Error("Réponse vide du serveur.");
      }
    } catch (err: unknown) {
      // Annulation volontaire → message neutre, pas une erreur. Le résultat
      // n'est pas défini (on sort avant setResult) → pas d'auto-save.
      if (controller.signal.aborted || (err instanceof Error && err.name === 'AbortError')) {
        toast.info('Analyse annulée.');
      } else {
        toast.error(friendlyError(err, 'processing'));
      }
    } finally {
      setIsLoading(false);
      analyzeAbortRef.current = null;
    }
  }, [
    hasVerbatims, selectedMethod, collectedVerbatims,
    structureMode, manualThemes, guideFile,
    rapportBlocked, openUpgrade, incrementLimit, setIsApercu, isAuthenticated, openAuthGate,
    saveDocument, verbatimCount,
  ]);

  // Annule l'analyse en cours (interrompt la requête réseau).
  const handleCancelAnalysis = useCallback(() => {
    analyzeAbortRef.current?.abort();
  }, []);

  // Reprise automatique : dès que l'utilisateur est connecté, on relance la
  // génération du rapport. Délai de stabilisation de session
  // (cf. AUTH_RESUME_SETTLE_MS) pour éviter un 401 après connexion Google.
  useEffect(() => {
    if (pendingAuthLaunch && isAuthenticated) {
      setPendingAuthLaunch(false);
      setTimeout(() => { void handleLaunchAnalysis(); }, AUTH_RESUME_SETTLE_MS);
    }
  }, [pendingAuthLaunch, isAuthenticated, handleLaunchAnalysis]);

  // ---- Word export ----
  const handleDownloadWord = useCallback(async () => {
    if (!result) return;
    const methodInfo = ANALYSIS_METHODS.find((m) => m.id === selectedMethod);
    const timestamp = new Date().toISOString().slice(0, 10);
    setIsExporting(true);
    try {
      await exportToWord({
        text: result,
        title: `Rapport d'analyse qualitative : ${methodInfo?.title ?? "Analyse"}`,
        method: methodInfo?.title ?? "",
        framework: methodInfo?.framework ?? "",
        reportFormat: "academic",
        filename: `qualyse_rapport_${methodInfo?.id ?? "analyse"}_${timestamp}.docx`,
      });
      toast.success("Document Word téléchargé.");
    } catch (err: unknown) {
      toast.error(friendlyError(err, 'export'));
    } finally {
      setIsExporting(false);
    }
  }, [result, selectedMethod]);

  // ---- Reset ----
  // Confirmation avant « Nouveau projet » — efface le brouillon sans filet.
  const [showResetConfirm, setShowResetConfirm] = useState(false);

  const handleReset = useCallback(() => {
    setActiveInput("upload");
    setVerbatimText("");
    setUploadedFiles([]);
    setStructureMode("ai");
    setManualThemes("");
    setGuideFile(null);
    setSelectedMethod("thematic-content");
    setResult(null);
    setIsApercu(false);
    setUploadProgress(null);
    setStep(1);
    setRestoredFromCache(false);
    setReportSavedAt(null);
    clearPersistedResult(REPORT_LS_KEY);
    toast.info("Formulaire réinitialisé.");
  }, []);

  // ---- Helpers ----
  function formatReportDate(ts: number | null): string {
    if (!ts) return "";
    return new Date(ts).toLocaleDateString("fr-FR", { day: "numeric", month: "long", year: "numeric" });
  }

  function formatFileSize(bytes: number): string {
    if (bytes < 1024) return `${bytes} o`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} Ko`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} Mo`;
  }

  // Extraction des titres réels du rapport généré.
  // Reconnaît les mêmes formats que le parser DOCX (route.ts /api/export/docx) :
  //   - "[TITRE 1] xxx" / "[TITRE 2] xxx" / "[TITRE 3] xxx" (optionnellement préfixé par #..####, avec \ échappé)
  //   - "# xxx" / "## xxx" / "### xxx" et leurs variantes échappées "\# xxx" etc.
  // Ne retourne que les niveaux 1 (thématiques principales) et 2 (sous-thématiques).
  // Aucun fallback générique : si rien n'est détecté, renvoie un tableau vide.
  function extractReportHeadings(text: string): Array<{ level: 1 | 2; text: string; id: string }> {
    if (!text) return [];
    const slug = (s: string, i: number): string => {
      const base = s
        .toLowerCase()
        .normalize("NFD")
        .replace(/[̀-ͯ]/g, "")
        .replace(/[^a-z0-9]+/g, "-")
        .replace(/^-+|-+$/g, "")
        .slice(0, 50);
      return `qly-r-h-${i}-${base || "h"}`;
    };
    const out: Array<{ level: 1 | 2; text: string; id: string }> = [];
    let idx = 0;
    for (const raw of text.split("\n")) {
      const line = raw.trim();
      if (!line) continue;

      // 1) Tag explicite [TITRE N] — éventuellement préfixé par # ou \#
      const tag = line.match(/^\\?#{0,4}\s*\[TITRE\s*([123])\]\s*(.+)/i);
      if (tag) {
        const lvl = parseInt(tag[1], 10);
        if (lvl === 1 || lvl === 2) {
          const t = tag[2].trim();
          if (t) { out.push({ level: lvl as 1 | 2, text: t, id: slug(t, idx) }); idx++; }
        }
        continue;
      }

      // 2) Niveau 3+ markdown ignoré (### et plus)
      if (/^\\?#{3,}\s+/.test(line)) continue;

      // 3) ## xxx ou \## xxx → niveau 2
      const md2 = line.match(/^\\?##\s+(.+)/);
      if (md2) {
        const t = md2[1].trim();
        if (t) { out.push({ level: 2, text: t, id: slug(t, idx) }); idx++; }
        continue;
      }

      // 4) # xxx ou \# xxx → niveau 1
      const md1 = line.match(/^\\?#\s+(.+)/);
      if (md1) {
        const t = md1[1].trim();
        if (t) { out.push({ level: 1, text: t, id: slug(t, idx) }); idx++; }
        continue;
      }
    }
    return out;
  }

  function estimatePages(text: string): number {
    return Math.max(1, Math.ceil(text.split(/\s+/).filter(Boolean).length / 275));
  }

  // ---- Stepper ----
  const M_COLOR = '#6a3500';
  const M_BG = '#fdf3e8';
  const M_STEPS = [
    { name: 'Données',   hint: 'Verbatims ou texte' },
    { name: 'Structure', hint: 'Guide & thèmes' },
    { name: 'Méthode',   hint: 'Approche analytique' },
    { name: 'Générer',   hint: 'Rapport complet' },
  ];
  const PTITLE: React.CSSProperties = {
    fontSize: 13, fontWeight: 700, color: '#6B7280', textTransform: 'uppercase',
    letterSpacing: '0.07em', fontFamily: 'monospace', marginBottom: 16,
    display: 'flex', alignItems: 'center', gap: 8,
  };

  // ---- JSX ----
  return (
    <div className="mx-auto w-full max-w-4xl" style={{ position: 'relative' }}>

      {/* BLOC 1 — Header sobre */}
      {!_compact && <div style={{ display: 'flex', alignItems: 'center', gap: 14, marginBottom: 24, padding: '20px 24px', borderRadius: 12, border: '1px solid #f5c4a0', background: '#fdf3e8' }}>
        <div style={{ width: 48, height: 48, borderRadius: 14, background: M_BG, border: `1.5px solid ${M_COLOR}40`, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
          <FileBarChart size={24} color={M_COLOR} />
        </div>
        <div>
          <div style={{ fontSize: 11, fontWeight: 700, fontFamily: 'monospace', letterSpacing: '0.1em', color: M_COLOR, textTransform: 'uppercase', marginBottom: 2 }}>MODULE 04</div>
          <div style={{ fontSize: 20, fontWeight: 800, color: '#1a1a1a', letterSpacing: '-0.3px', lineHeight: 1.2 }}>Rapport d&apos;analyse qualitative</div>
          <div style={{ fontSize: 13, color: '#6B7280', marginTop: 4 }}>Qualyse transforme vos données qualitatives brutes en rapports structurés. À partir de vos transcriptions ou de vos fichiers de codification, choisissez l&apos;approche qui vous convient pour produire un rapport d&apos;analyse organisé par thématiques et appuyé sur des verbatims, prêt à être intégré dans votre livrable final.</div>
        </div>
      </div>}

      {/* STEPPER */}
      <div className="qly-stepper" style={{ display: 'flex', gap: 6, marginBottom: 20, alignItems: 'stretch' }}>
        {M_STEPS.map((s, i) => {
          const n = i + 1;
          const isActive = step === n;
          const isDone = step > n;
          return (
            <div key={n} className="qly-step-item" style={{ display: 'flex', alignItems: 'center', flex: 1, minWidth: 0 }}>
              {i > 0 && (
                <span className="qly-step-sep" style={{ padding: '0 3px', fontSize: 24, color: '#BBBBBB', fontWeight: 900, flexShrink: 0, userSelect: 'none', lineHeight: 1 }}>›</span>
              )}
              <div
                onClick={() => isDone && setStep(n)}
                style={{
                  flex: 1, display: 'flex', alignItems: 'center', gap: 10,
                  padding: '12px 14px', borderRadius: 10, minWidth: 0,
                  border: isActive ? `2px solid ${M_COLOR}` : isDone ? `1.5px solid ${M_COLOR}55` : '1.5px solid #DEDEDE',
                  background: isActive ? M_BG : isDone ? `${M_COLOR}08` : 'white',
                  boxShadow: isActive ? `0 4px 14px ${M_COLOR}35` : 'none',
                  cursor: isDone ? 'pointer' : 'default', transition: 'all 0.2s',
                }}
              >
                <div style={{
                  width: 28, height: 28, borderRadius: '50%', flexShrink: 0,
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  fontSize: 12, fontWeight: 800,
                  background: (isActive || isDone) ? M_COLOR : 'white',
                  border: (isActive || isDone) ? 'none' : `2px solid ${M_COLOR}`,
                  color: (isActive || isDone) ? 'white' : M_COLOR,
                }}>
                  {isDone ? '✓' : n}
                </div>
                <div style={{ minWidth: 0, overflow: 'hidden' }}>
                  <div style={{ fontSize: 12, fontWeight: 700, color: (isActive || isDone) ? M_COLOR : '#555', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{s.name}</div>
                  <div className="qly-step-hint" style={{ fontSize: 10, color: '#6B7280', marginTop: 1, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{s.hint}</div>
                </div>
              </div>
            </div>
          );
        })}
      </div>

      {/* MAIN CARD */}
      <div className="qly-module-card" style={{ background: 'white', borderRadius: 20, border: '1px solid #E5E7EB', overflow: 'hidden', boxShadow: '0 4px 24px rgba(0,0,0,0.05)' }}>

        {/* STEP 1 — Données */}
        {step === 1 && (
          <div className="qly-step-content" style={{ padding: '28px', display: 'flex', flexDirection: 'column', gap: 14 }}>
            <div style={PTITLE}>
              <div style={{ width: 3, height: 14, borderRadius: 2, background: M_COLOR, flexShrink: 0 }} />
              Étape 1 — Importez vos données
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3,1fr)', gap: 10, marginBottom: 4 }}>
              {[
                { icon: '📂', title: '20+ fichiers simultanés', sub: 'Uploadez tout votre corpus en une fois' },
                { icon: '🎙️', title: 'Transcriptions & entretiens', sub: 'Fichiers .docx issus de vos enquêtes terrain' },
                { icon: '📊', title: 'Fichiers Excel codifiés', sub: 'Matrices avec verbatims et codes thématiques' },
              ].map(c => (
                <div key={c.title} style={{ display: 'flex', alignItems: 'flex-start', gap: 10, padding: '12px 14px', background: M_BG, borderRadius: 10, border: `1px solid ${M_COLOR}35` }}>
                  <span style={{ fontSize: 20, lineHeight: 1, flexShrink: 0, marginTop: 1 }}>{c.icon}</span>
                  <div>
                    <p style={{ fontSize: 12, fontWeight: 700, color: M_COLOR, margin: '0 0 2px', lineHeight: 1.3 }}>{c.title}</p>
                    <p style={{ fontSize: 11, color: '#7a4f20', margin: 0, lineHeight: 1.4 }}>{c.sub}</p>
                  </div>
                </div>
              ))}
            </div>
            <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
              <button type="button" onClick={() => setActiveInput('upload')}
                style={{ display: 'flex', alignItems: 'center', gap: 7, padding: '9px 18px', borderRadius: 8, fontWeight: 600, fontSize: 13, cursor: 'pointer', border: `2px solid ${M_COLOR}`, background: activeInput === 'upload' ? M_COLOR : '#fff', color: activeInput === 'upload' ? '#fff' : M_COLOR, transition: 'all .15s' }}>
                <Upload size={15} />Importer des fichiers
              </button>
              <button type="button" onClick={() => setActiveInput('paste')}
                style={{ display: 'flex', alignItems: 'center', gap: 7, padding: '9px 18px', borderRadius: 8, fontWeight: 600, fontSize: 13, cursor: 'pointer', border: `2px solid ${M_COLOR}`, background: activeInput === 'paste' ? M_COLOR : '#fff', color: activeInput === 'paste' ? '#fff' : M_COLOR, transition: 'all .15s' }}>
                <FileText size={15} />Coller du texte
              </button>
            </div>
            {activeInput === 'upload' && (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                <div role="button" tabIndex={0}
                  onClick={() => fileInputRef.current?.click()}
                  onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') fileInputRef.current?.click(); }}
                  style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', border: `2px dashed ${M_COLOR}`, borderRadius: 10, background: M_BG, padding: '36px 24px', cursor: 'pointer', textAlign: 'center', gap: 6, transition: 'background .15s' }}
                  onMouseEnter={e => (e.currentTarget.style.background = '#f5e5cc')}
                  onMouseLeave={e => (e.currentTarget.style.background = M_BG)}>
                  <Upload size={28} color={M_COLOR} style={{ marginBottom: 4 }} />
                  <p style={{ fontSize: 14, fontWeight: 600, color: M_COLOR, margin: 0 }}>Glissez-déposez vos fichiers ici</p>
                  <p style={{ fontSize: 13, color: '#374151', margin: 0 }}>ou cliquez pour parcourir</p>
                  <p style={{ fontSize: 12, color: '#6B7280', margin: '4px 0 0' }}>Formats acceptés : .docx, .xlsx &nbsp;&middot;&nbsp; Plusieurs fichiers possibles</p>
                </div>
                <input ref={fileInputRef} type="file" multiple accept=".docx,.doc,.xlsx,application/vnd.openxmlformats-officedocument.wordprocessingml.document,application/msword,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" onChange={handleFileUpload} className="hidden" />
                {uploadProgress !== null && (
                  <div style={{ borderRadius: 10, border: `1.5px solid ${uploadAllDone ? '#16a34a' : M_COLOR}`, background: uploadAllDone ? '#f0fdf4' : M_BG, padding: '12px 16px', display: 'flex', flexDirection: 'column', gap: 8 }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <span style={{ fontSize: 13, fontWeight: 600, color: uploadAllDone ? '#16a34a' : M_COLOR }}>
                        {uploadAllDone
                          ? `${uploadProgress.total} fichier${uploadProgress.total > 1 ? 's' : ''} prêt${uploadProgress.total > 1 ? 's' : ''} ✓ — vous pouvez lancer l'analyse`
                          : `Téléversement en cours… ${uploadProgress.done}/${uploadProgress.total} fichiers`}
                      </span>
                      {isUploading && <Loader2 size={14} color={M_COLOR} className="animate-spin" />}
                    </div>
                    <div style={{ height: 6, background: '#D1D5DB', borderRadius: 3, overflow: 'hidden' }}>
                      <div style={{ height: '100%', borderRadius: 3, background: uploadAllDone ? '#16a34a' : M_COLOR, width: `${Math.round((uploadProgress.done / uploadProgress.total) * 100)}%`, transition: 'width 0.25s ease' }} />
                    </div>
                  </div>
                )}
                {uploadedFiles.length > 0 && (
                  <div style={{ border: '1px solid #E5E7EB', borderRadius: 10, overflow: 'hidden' }}>
                    <div style={{ padding: '8px 14px', borderBottom: '1px solid #F3F4F6', background: '#F9FAFB' }}>
                      <span style={{ fontSize: 12, fontWeight: 600, color: '#374151' }}>Fichiers chargés ({uploadedFiles.length})</span>
                    </div>
                    <div style={{ maxHeight: 180, overflowY: 'auto' }}>
                      {uploadedFiles.map((file, idx) => (
                        <div key={file.name + idx} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '8px 14px', borderBottom: idx < uploadedFiles.length - 1 ? '1px solid #F3F4F6' : 'none' }}>
                          <div style={{ display: 'flex', alignItems: 'center', gap: 8, minWidth: 0 }}>
                            <FileText size={14} color={M_COLOR} />
                            <div style={{ minWidth: 0 }}>
                              <p style={{ fontSize: 12, fontWeight: 500, color: '#111827', margin: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{file.name}</p>
                              <p style={{ fontSize: 11, color: '#6B7280', margin: 0 }}>{formatFileSize(file.size)}</p>
                            </div>
                          </div>
                          <button type="button" onClick={() => removeFile(idx)} style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 4, borderRadius: 6, flexShrink: 0 }}>
                            <X size={14} color="#9CA3AF" />
                          </button>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            )}
            {activeInput === 'paste' && (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                <Textarea
                  placeholder="Collez directement vos verbatims ici..."
                  value={verbatimText}
                  onChange={(e) => setVerbatimText(e.target.value)}
                  style={{ minHeight: 200, resize: 'vertical', fontSize: 13, lineHeight: 1.65, border: `2px dashed ${M_COLOR}`, borderRadius: 10 }}
                />
                {verbatimText.trim().length > 0 && (
                  <p style={{ fontSize: 11, color: '#6B7280', margin: 0 }}>
                    {verbatimText.trim().split(/\s+/).filter(Boolean).length} mots &middot; {verbatimText.length} caractères
                  </p>
                )}
              </div>
            )}
            {hasVerbatims && (
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '8px 12px', background: '#f5e5cc', borderRadius: 8 }}>
                <CheckCircle2 size={15} color={M_COLOR} />
                <span style={{ fontSize: 12, fontWeight: 600, color: M_COLOR }}>
                  {collectedVerbatims.length} source{collectedVerbatims.length > 1 ? 's' : ''} prête{collectedVerbatims.length > 1 ? 's' : ''} pour l&apos;analyse
                </span>
              </div>
            )}
          </div>
        )}

        {/* STEP 2 — Structure */}
        {step === 2 && (
          <div className="qly-step-content" style={{ padding: '28px', display: 'flex', flexDirection: 'column', gap: 14 }}>
            <div style={PTITLE}>
              <div style={{ width: 3, height: 14, borderRadius: 2, background: M_COLOR, flexShrink: 0 }} />
              Étape 2 — Définir la structure de l&apos;analyse
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3,1fr)', gap: 12 }}>
              <button type="button" onClick={() => setStructureMode('guide')}
                style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 8, padding: '18px 14px', borderRadius: 10, border: structureMode === 'guide' ? `2px solid ${M_COLOR}` : '2px solid #E5E7EB', background: structureMode === 'guide' ? M_BG : '#fff', cursor: 'pointer', textAlign: 'center', transition: 'all .15s' }}>
                <div style={{ width: 38, height: 38, borderRadius: 10, background: structureMode === 'guide' ? M_COLOR : '#F3F4F6', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  <Paperclip size={18} color={structureMode === 'guide' ? '#fff' : '#6B7280'} />
                </div>
                <p style={{ fontSize: 13, fontWeight: 700, color: '#111827', margin: 0 }}>Uploader un document</p>
                <p style={{ fontSize: 12, color: '#6B7280', margin: 0, lineHeight: 1.5 }}>Guide d&apos;entretien, TDR, cadre logique… Qualyse en extrait les thématiques.</p>
              </button>
              <button type="button" onClick={() => setStructureMode('manual')}
                style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 8, padding: '18px 14px', borderRadius: 10, border: structureMode === 'manual' ? `2px solid ${M_COLOR}` : '2px solid #E5E7EB', background: structureMode === 'manual' ? M_BG : '#fff', cursor: 'pointer', textAlign: 'center', transition: 'all .15s' }}>
                <div style={{ width: 38, height: 38, borderRadius: 10, background: structureMode === 'manual' ? M_COLOR : '#F3F4F6', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  <PenLine size={18} color={structureMode === 'manual' ? '#fff' : '#6B7280'} />
                </div>
                <p style={{ fontSize: 13, fontWeight: 700, color: '#111827', margin: 0 }}>Définir mes thèmes</p>
                <p style={{ fontSize: 12, color: '#6B7280', margin: 0, lineHeight: 1.5 }}>Saisissez vos thèmes manuellement.</p>
              </button>
              <button type="button" onClick={() => setStructureMode('ai')}
                style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 8, padding: '18px 14px', borderRadius: 10, border: structureMode === 'ai' ? `2px solid ${M_COLOR}` : '2px solid #E5E7EB', background: structureMode === 'ai' ? '#f5e5cc' : '#fff', cursor: 'pointer', textAlign: 'center', transition: 'all .15s' }}>
                <div style={{ width: 38, height: 38, borderRadius: 10, background: structureMode === 'ai' ? M_COLOR : '#F3F4F6', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  <Sparkles size={18} color={structureMode === 'ai' ? '#fff' : '#6B7280'} />
                </div>
                <p style={{ fontSize: 13, fontWeight: 700, color: '#111827', margin: 0 }}>Qualyse propose</p>
                <p style={{ fontSize: 12, color: '#6B7280', margin: 0, lineHeight: 1.5 }}>Thèmes émergents automatiques.</p>
              </button>
            </div>
            {structureMode === 'guide' && (
              <div>
                {guideFile ? (
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '10px 14px', borderRadius: 8, background: M_BG, border: `1.5px solid ${M_COLOR}` }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8, minWidth: 0 }}>
                      <FileText size={14} color={M_COLOR} />
                      <p style={{ fontSize: 12, fontWeight: 500, color: '#111827', margin: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{guideFile.name}</p>
                    </div>
                    <button type="button" onClick={() => setGuideFile(null)} style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 4, flexShrink: 0, marginLeft: 8 }}>
                      <X size={14} color="#6B7280" />
                    </button>
                  </div>
                ) : (
                  <div role="button" tabIndex={0}
                    onClick={() => guideInputRef.current?.click()}
                    onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') guideInputRef.current?.click(); }}
                    style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', padding: '18px', borderRadius: 10, border: `2px dashed ${M_COLOR}`, background: M_BG, cursor: 'pointer', gap: 4, textAlign: 'center' }}>
                    <Upload size={16} color={M_COLOR} />
                    <p style={{ fontSize: 13, color: M_COLOR, fontWeight: 600, margin: 0 }}>Glissez votre document ici</p>
                    <p style={{ fontSize: 11, color: '#6B7280', margin: 0 }}>.docx, .pdf, .txt</p>
                  </div>
                )}
                <input ref={guideInputRef} type="file" accept=".txt,.docx,.doc,.pdf,text/plain,application/vnd.openxmlformats-officedocument.wordprocessingml.document,application/msword,application/pdf" onChange={handleGuideUpload} className="hidden" />
              </div>
            )}
            {structureMode === 'manual' && (
              <Textarea
                placeholder={"Ex:\n1. Accès aux ressources\n   1.1 Ressources financières\n2. Dynamiques communautaires"}
                value={manualThemes}
                onChange={(e) => setManualThemes(e.target.value)}
                className="font-mono"
                style={{ minHeight: 120, fontSize: 12, resize: 'vertical', border: '2px solid #E5E7EB', borderRadius: 10 }}
              />
            )}
          </div>
        )}

        {/* STEP 3 — Méthode */}
        {step === 3 && (
          <div className="qly-step-content" style={{ padding: '28px', display: 'flex', flexDirection: 'column', gap: 14 }}>
            <div style={PTITLE}>
              <div style={{ width: 3, height: 14, borderRadius: 2, background: M_COLOR, flexShrink: 0 }} />
              Étape 3 — Choisir la méthode d&apos;analyse
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
              {ANALYSIS_METHODS.map((method) => {
                const Icon = method.icon;
                const sel = selectedMethod === method.id;
                return (
                  <button key={method.id} type="button" onClick={() => setSelectedMethod(method.id)}
                    style={{ display: 'flex', alignItems: 'flex-start', gap: 12, padding: '14px 16px', borderRadius: 10, border: sel ? `2px solid ${M_COLOR}` : '1.5px solid #E5E7EB', background: sel ? M_BG : '#fff', cursor: 'pointer', textAlign: 'left', width: '100%', transition: 'all .15s' }}
                    onMouseEnter={e => { if (!sel) e.currentTarget.style.borderColor = M_COLOR; }}
                    onMouseLeave={e => { if (!sel) e.currentTarget.style.borderColor = '#E5E7EB'; }}>
                    <div style={{ width: 18, height: 18, borderRadius: '50%', border: sel ? `5px solid ${M_COLOR}` : '2px solid #9CA3AF', flexShrink: 0, marginTop: 3, transition: 'all .15s' }} />
                    <div style={{ width: 34, height: 34, borderRadius: 8, background: sel ? M_COLOR : '#F3F4F6', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                      <Icon size={15} color={sel ? '#fff' : '#6B7280'} />
                    </div>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <p style={{ fontSize: 13, fontWeight: 700, color: '#111827', margin: '0 0 2px', lineHeight: 1.3 }}>{method.title}</p>
                      <p style={{ fontSize: 11, color: '#6B7280', margin: '0 0 4px', fontStyle: 'italic' }}>{method.framework}</p>
                      <p style={{ fontSize: 11, color: '#6B7280', margin: 0, lineHeight: 1.5 }}>{method.description}</p>
                    </div>
                  </button>
                );
              })}
            </div>
          </div>
        )}

        {/* STEP 4 — Générer */}
        {step === 4 && (
          <div className="qly-step-content" style={{ padding: '28px', display: 'flex', flexDirection: 'column', gap: 16 }}>
            <div style={PTITLE}>
              <div style={{ width: 3, height: 14, borderRadius: 2, background: M_COLOR, flexShrink: 0 }} />
              Étape 4 — Vérification et génération
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginBottom: 8 }}>
              {[
                { label: 'Données', val: collectedVerbatims.length > 0 ? `${collectedVerbatims.length} source${collectedVerbatims.length > 1 ? 's' : ''}` : 'Aucun fichier importé' },
                { label: 'Structure', val: structureMode === 'guide' ? (guideFile ? 'Guide importé' : 'En attente du guide') : structureMode === 'manual' ? (manualThemes.trim() ? 'Thèmes définis' : 'Thèmes non définis') : 'Proposés par Qualyse' },
                { label: 'Méthode', val: ANALYSIS_METHODS.find(m => m.id === selectedMethod)?.title ?? '—' },
                { label: 'Format', val: 'Rapport académique' },
              ].map(({ label, val }) => (
                <div key={label} style={{ padding: '14px 16px', background: '#FAFAF8', borderRadius: 10, border: '1px solid #E5E7EB' }}>
                  <div style={{ fontSize: 10, fontWeight: 700, color: '#6B7280', textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 5 }}>{label}</div>
                  <div style={{ fontSize: 13, fontWeight: 700, color: '#111827' }}>{val}</div>
                </div>
              ))}
            </div>
            {!result && (
              <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 8 }}>
                {/* Conseil d'optimisation des crédits — non bloquant. */}
                {showOptimizationTip && (
                  <div style={{ display: 'flex', alignItems: 'flex-start', gap: 10, padding: '10px 14px', borderRadius: 8, background: '#FFFBEB', border: '1px solid #FDE68A', width: '100%', maxWidth: 560 }}>
                    <span style={{ fontSize: 16, lineHeight: 1, flexShrink: 0 }}>💡</span>
                    <div style={{ flex: 1, fontSize: 12.5, color: '#78350F', lineHeight: 1.5 }}>
                      Vous pouvez analyser jusqu&apos;à <strong>{tipMaxEntretiens} entretiens</strong> pour le même coût de <strong>{tipFlatCost} crédits</strong>. Voulez-vous ajouter plus d&apos;entretiens ?
                    </div>
                  </div>
                )}
                <div style={{ width: '100%', maxWidth: 520 }}>
                  <CreditCostBanner moduleType="analysis" />
                </div>
                <button type="button"
                  disabled={!hasVerbatims || isLoading}
                  onClick={handleLaunchAnalysis}
                  style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 10, width: '100%', maxWidth: 520, padding: '16px 32px', borderRadius: 10, border: 'none', background: M_COLOR, color: '#fff', fontSize: 16, fontWeight: 700, cursor: hasVerbatims && !isLoading ? 'pointer' : 'not-allowed', opacity: hasVerbatims && !isLoading ? 1 : 0.45, transition: 'opacity .15s', boxShadow: '0 4px 16px rgba(27,67,50,.25)' }}>
                  {isLoading ? (
                    <><Loader2 size={20} className="animate-spin" />Génération en cours...</>
                  ) : (
                    <><FileText size={20} />{rapportBlocked ? 'APERÇU GRATUIT' : 'GÉNÉRER LE RAPPORT'}</>
                  )}
                </button>
                {!hasVerbatims && !isLoading && (
                  <p style={{ fontSize: 12, color: '#6B7280', margin: 0 }}>Importez vos données à l&apos;étape 1 pour activer la génération</p>
                )}
                {isLoading && (
                  <div style={{ width: '100%', maxWidth: 520 }}>
                    <div style={{ height: 6, background: '#E5E7EB', borderRadius: 6, overflow: 'hidden', marginTop: 8 }}>
                      <div className="animate-pulse" style={{ height: '100%', background: M_COLOR, borderRadius: 6, width: '60%' }} />
                    </div>
                    <p style={{ fontSize: 12, color: '#6B7280', textAlign: 'center', marginTop: 8 }}>Qualyse analyse vos verbatims et construit la grille thématique…</p>
                    <div style={{ display: 'flex', justifyContent: 'center', marginTop: 10 }}>
                      <button type="button" onClick={handleCancelAnalysis}
                        style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '7px 16px', borderRadius: 8, border: '1px solid #E5E7EB', background: '#fff', color: '#6B7280', fontSize: 13, fontWeight: 600, cursor: 'pointer', fontFamily: 'inherit' }}>
                        <X size={14} /> Annuler
                      </button>
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* Historique récent (48 h) — rapports sauvegardés, ré-ouvrables. */}
            {!result && (
              <ModuleHistoryPanel
                history={history}
                accent={M_COLOR}
                noun="rapport"
                onReopen={(doc) => { setResult(doc.content); setIsApercu(false); setStep(4); }}
              />
            )}

            {restoredFromCache && result && !isApercu && (
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, padding: '10px 14px', background: '#EFF6FF', border: '1px solid #BFDBFE', borderRadius: 8, fontSize: 13, color: '#1E40AF', marginBottom: 12 }}>
                <span>
                  Votre dernier rapport{reportSavedAt ? ` — ${formatReportDate(reportSavedAt)}` : ''} — téléchargez-le ci-dessous ou démarrez un nouveau projet.
                </span>
                <button onClick={dismissRestoredBanner} style={{ background: 'transparent', border: '1px solid #BFDBFE', color: '#1E40AF', padding: '4px 10px', borderRadius: 6, cursor: 'pointer', fontSize: 12, fontWeight: 600, flexShrink: 0 }}>Effacer</button>
              </div>
            )}
            {result && !isApercu && (() => {
              const headings = extractReportHeadings(result);
              return (
              <div style={{ background: M_BG, border: `2px solid ${M_COLOR}`, borderRadius: 12, padding: '24px' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 16 }}>
                  <CheckCircle2 size={22} color={M_COLOR} />
                  <span style={{ fontSize: 16, fontWeight: 700, color: M_COLOR }}>Rapport généré avec succès</span>
                </div>
                <p style={{ fontSize: 13, fontWeight: 700, color: '#374151', margin: '0 0 10px', display: 'flex', alignItems: 'center', gap: 7 }}>
                  <FileBarChart size={15} color={M_COLOR} />Sommaire du rapport :
                </p>
                {headings.length > 0 ? (
                  <div style={{
                    maxHeight: 160, overflowY: 'auto',
                    background: '#fff', border: '1px solid #e5e7eb', borderRadius: 8,
                    padding: '10px 12px', margin: '0 0 20px',
                  }}>
                    <ul style={{ listStyle: 'none', padding: 0, margin: 0, display: 'flex', flexDirection: 'column', gap: 4 }}>
                      {headings.map((h) => (
                        <li key={h.id} style={{ paddingLeft: h.level === 2 ? 18 : 0 }}>
                          <div style={{
                            display: 'flex', alignItems: 'flex-start', gap: 8,
                            padding: '3px 4px',
                            fontSize: h.level === 1 ? 13 : 12.5,
                            fontWeight: h.level === 1 ? 700 : 500,
                            color: h.level === 1 ? '#1a1a1a' : '#374151',
                          }}>
                            <span style={{ color: M_COLOR, fontWeight: 700, flexShrink: 0, fontFamily: 'monospace', minWidth: h.level === 1 ? 14 : 10, textAlign: 'right' }}>
                              {h.level === 1 ? '—' : '·'}
                            </span>
                            <span style={{ flex: 1 }}>{h.text}</span>
                          </div>
                        </li>
                      ))}
                    </ul>
                  </div>
                ) : (
                  <p style={{ fontSize: 12, color: '#6B7280', fontStyle: 'italic', margin: '0 0 20px' }}>
                    Aucun titre détecté dans le rapport généré.
                  </p>
                )}

                <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 20, padding: '8px 12px', background: '#fff', borderRadius: 8, border: '1px solid #D1FAE5' }}>
                  <FileText size={14} color={M_COLOR} />
                  <span style={{ fontSize: 13, color: '#374151' }}>Pages estimées : <strong style={{ color: M_COLOR }}>{estimatePages(result)} page{estimatePages(result) > 1 ? 's' : ''}</strong></span>
                </div>

                <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap' }}>
                  <button type="button" onClick={handleDownloadWord} disabled={isExporting}
                    style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '11px 22px', borderRadius: 9, border: 'none', background: M_COLOR, color: '#fff', fontSize: 14, fontWeight: 700, cursor: isExporting ? 'wait' : 'pointer', opacity: isExporting ? 0.7 : 1 }}>
                    {isExporting ? <Loader2 size={16} className="animate-spin" /> : <Download size={16} />}
                    {isExporting ? 'Génération…' : 'Télécharger en Word (.docx)'}
                  </button>
                  <button type="button" onClick={() => setShowResetConfirm(true)}
                    style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '11px 22px', borderRadius: 9, border: `2px solid ${M_COLOR}`, background: '#fff', color: M_COLOR, fontSize: 14, fontWeight: 700, cursor: 'pointer' }}>
                    <RotateCcw size={16} />Nouveau rapport
                  </button>
                </div>
              </div>
              );
            })()}
            {result && isApercu && (() => {
              const methodInfo = ANALYSIS_METHODS.find(m => m.id === selectedMethod);
              return (
                <div style={{ background: '#fff', border: `2px solid ${M_COLOR}`, borderRadius: 12, padding: '24px', position: 'relative' }}>
                  <div style={{ position: 'absolute', top: 16, right: 16, background: M_BG, border: `1px solid ${M_COLOR}`, borderRadius: 6, padding: '3px 10px', fontSize: 11, fontWeight: 700, color: M_COLOR }}>
                    🔓 Aperçu gratuit
                  </div>
                  <div style={{ marginBottom: 16 }}>
                    <p style={{ fontSize: 13, fontWeight: 800, color: M_COLOR, margin: '0 0 4px', letterSpacing: '0.05em' }}>📊 APERÇU DE VOTRE RAPPORT</p>
                    <p style={{ fontSize: 12, color: '#6B7280', margin: '0 0 8px', fontStyle: 'italic' }}>Analyse {methodInfo?.title ?? selectedMethod}</p>
                    <div style={{ height: 1, background: '#ffe0b8' }} />
                  </div>
                  <p style={{ fontSize: 13, color: '#374151', margin: '0 0 12px' }}>
                    Qualyse a analysé vos <strong>{collectedVerbatims.length}</strong> fichier{collectedVerbatims.length > 1 ? 's' : ''} et identifié les thèmes principaux :
                  </p>
                  <div style={{ background: '#F9FAFB', borderRadius: 8, padding: '14px 16px', marginBottom: 16, fontFamily: 'monospace', fontSize: 12, color: '#374151', lineHeight: 1.8, whiteSpace: 'pre-wrap' }}>{result}</div>
                  <div style={{ display: 'flex', gap: 16, marginBottom: 20, flexWrap: 'wrap' }}>
                    <span style={{ fontSize: 12, color: '#6B7280' }}>📄 Rapport estimé : 15-20 pages</span>
                    <span style={{ fontSize: 12, color: '#6B7280' }}>⏱️ Temps de génération : ~3 min</span>
                    <span style={{ fontSize: 12, color: '#6B7280' }}>📝 Verbatims : {collectedVerbatims.reduce((acc, v) => acc + v.split(/\s+/).filter(Boolean).length, 0)} mots</span>
                  </div>
                  <div style={{ background: M_BG, border: `1.5px solid ${M_COLOR}`, borderRadius: 10, padding: '18px 20px' }}>
                    <p style={{ fontSize: 13, color: '#374151', margin: '0 0 4px', fontWeight: 600 }}>🔒 Votre rapport complet avec analyse approfondie, verbatims et recommandations est prêt.</p>
                    {/* Prix de référence UNIQUE (D-020) — « 20$ » = faute D-019,
                        « 13 000 FCFA » = montant FAUX (réel : 13 100). */}
                    <p style={{ fontSize: 13, color: M_COLOR, fontWeight: 700, margin: '12px 0 4px' }}>⭐ Plan Pro — {REFERENCE_PRICE_LABEL}</p>
                    <p style={{ fontSize: 12, color: '#6B7280', margin: '0 0 16px' }}>Montant local affiché au moment du paiement</p>
                    <button
                      onClick={isAuthenticated ? openUpgrade : () => openAuth('register')}
                      style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8, width: '100%', padding: '13px 20px', borderRadius: 9, border: 'none', background: M_COLOR, color: '#fff', fontSize: 14, fontWeight: 700, cursor: 'pointer' }}>
                      🚀 Générer mon rapport complet → {isAuthenticated ? 'Passer au Pro' : "S'inscrire et générer"}
                    </button>
                  </div>
                  <button type="button" onClick={() => setShowResetConfirm(true)}
                    style={{ display: 'flex', alignItems: 'center', gap: 6, marginTop: 12, padding: '8px 14px', borderRadius: 8, border: '1.5px solid #E5E7EB', background: '#fff', color: '#6B7280', fontSize: 12, fontWeight: 600, cursor: 'pointer' }}>
                    <RotateCcw size={13} />Nouveau rapport
                  </button>
                </div>
              );
            })()}
          </div>
        )}

        {/* BOTTOM NAV */}
        <div className="qly-nav-bar" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '16px 28px', borderTop: '1px solid #E5E7EB', background: '#FAFAF8' }}>
          <button
            disabled={step === 1}
            onClick={() => setStep(s => s - 1)}
            style={{ padding: '10px 20px', background: 'transparent', color: step === 1 ? '#D1D5DB' : '#6B7280', border: `1.5px solid ${step === 1 ? '#E5E7EB' : '#D1D5DB'}`, borderRadius: 8, fontSize: 13, fontWeight: 600, cursor: step === 1 ? 'default' : 'pointer', fontFamily: 'inherit', transition: 'all 0.15s' }}
          >← Retour</button>
          <span style={{ fontSize: 12, color: '#6B7280' }}>Étape {step} sur {M_STEPS.length}</span>
          {step < M_STEPS.length ? (
            <button
              onClick={() => setStep(s => s + 1)}
              disabled={isUploading}
              style={{ padding: '10px 24px', background: isUploading ? '#D1D5DB' : M_COLOR, color: 'white', border: 'none', borderRadius: 8, fontSize: 13, fontWeight: 700, cursor: isUploading ? 'not-allowed' : 'pointer', fontFamily: 'inherit', display: 'flex', alignItems: 'center', gap: 8, transition: 'background 0.15s' }}
            >Continuer →</button>
          ) : <div style={{ width: 110 }} />}
        </div>
      </div>

      {/* Confirmation « Nouveau rapport » — évite la perte accidentelle du rapport. */}
      <AlertDialog open={showResetConfirm} onOpenChange={setShowResetConfirm}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Démarrer un nouveau rapport ?</AlertDialogTitle>
            <AlertDialogDescription>
              Le rapport actuel sera définitivement perdu. Cette action est irréversible.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Annuler</AlertDialogCancel>
            <AlertDialogAction onClick={() => { setShowResetConfirm(false); handleReset(); }}>
              Démarrer un nouveau rapport
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
